import {
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { cn } from "../lib/utils";
import { profilePhotoObjectStyle } from "../lib/profile-photo-style";

type ImgPhase = "loading" | "loaded" | "error";

export type ImageWithSkeletonProps = {
  src: string;
  alt: string;
  /** Conteneur : taille, coins (`rounded-full`, `rounded-lg`), bordure */
  className?: string;
  /** Classes additionnelles pour la balise `img` (ex. `object-contain`) */
  imageClassName?: string;
  /** Style inline pour la balise `img` (ex. `objectPosition`) */
  imageStyle?: CSSProperties;
  /** Recadrage photo de profil (`object-position` CSS). Fusionné dans `imageStyle`. */
  imagePosition?: string | null;
  fallback?: ReactNode;
};

/** Image avec squelette pendant le chargement (évite le flash avant affichage). */
export const ImageWithSkeleton = ({
  src,
  alt,
  className,
  imageClassName,
  imageStyle,
  imagePosition,
  fallback,
}: ImageWithSkeletonProps) => {
  const [phase, setPhase] = useState<ImgPhase>("loading");

  useEffect(() => {
    setPhase("loading");
  }, [src]);

  let content: ReactNode;
  if (phase === "error" && fallback != null) {
    content = (
      <span className="relative z-10 flex h-full w-full items-center justify-center overflow-hidden">
        {fallback}
      </span>
    );
  } else if (phase === "error") {
    content = (
      <span
        className="bg-muted relative z-10 block h-full w-full"
        aria-hidden
      />
    );
  } else {
    content = (
      <img
        src={src}
        alt={alt}
        onLoad={() => setPhase("loaded")}
        onError={() => setPhase("error")}
        style={{ ...profilePhotoObjectStyle(imagePosition), ...imageStyle }}
        className={cn(
          "relative z-10 h-full w-full transition-opacity duration-150",
          imageClassName ?? "object-cover",
          phase === "loaded" ? "opacity-100" : "opacity-0",
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 overflow-hidden",
        className,
      )}
    >
      {phase === "loading" && (
        <span
          className="bg-muted absolute inset-0 z-0 animate-pulse"
          aria-hidden
        />
      )}
      {content}
    </span>
  );
};

/** Variante circulaire (avatars, logos ronds). */
export const CircleImageWithSkeleton = ({
  className,
  ...props
}: ImageWithSkeletonProps) => {
  return (
    <ImageWithSkeleton {...props} className={cn("rounded-full", className)} />
  );
};

type RadixImageStatus = "idle" | "loading" | "loaded" | "error";

/** Avatar Radix : squelette neutre au chargement ; `fallback` si erreur ou pas d’URL. */
export const AvatarWithSkeleton = ({
  src,
  alt,
  className,
  fallback,
  imagePosition,
  ...avatarProps
}: {
  src?: string | null;
  alt: string;
  className?: string;
  fallback: ReactNode;
  /** Recadrage photo de profil (`object-position` CSS). */
  imagePosition?: string | null;
} & ComponentPropsWithoutRef<typeof Avatar>) => {
  const [status, setStatus] = useState<RadixImageStatus>("idle");
  const positionStyle = profilePhotoObjectStyle(imagePosition);

  useEffect(() => {
    setStatus("idle");
  }, [src]);

  return (
    <Avatar className={className} {...avatarProps}>
      {src ? (
        <AvatarImage
          src={src}
          alt={alt}
          style={positionStyle}
          className={
            positionStyle
              ? "aspect-square h-full w-full object-cover"
              : undefined
          }
          onLoadingStatusChange={setStatus}
        />
      ) : null}
      <AvatarFallback delayMs={0} className="overflow-hidden">
        {src && status !== "error" ? (
          <span
            className="block h-full w-full animate-pulse bg-muted"
            aria-hidden
          />
        ) : (
          fallback
        )}
      </AvatarFallback>
    </Avatar>
  );
};
